import {DateTime} from 'luxon';
import {fetchPostAuthSafe} from '../../app/utils';

/**
 * Zone changes through the /mapview zone routes (ops-runner zone service): request helpers, the ops rules
 * (which ops are dated) and the date rules the save dialog follows. The server decides again; these only drive
 * the UI.
 */

// How far back anyone who can edit zones may date a change themselves
export const SELF_SERVE_DAYS = 0; // only today's changes are applied directly; backdated ones go to support

// zoneFreezeDate when there is no freeze
const NO_FREEZE = '2000-01-01';

const DATED_OP_TYPES = ['createBlock', 'archiveBlock', 'restoreBlock', 'createArea', 'archiveArea', 'restoreArea'];

async function readJson(response) {
  try {
    return await response.json();
  } catch (err) {
    return {};
  }
}

async function getJson(url) {
  try {
    const response = await fetch(url, {cache: 'no-store'});
    return {ok: response.ok, status: response.status, body: await readJson(response)};
  } catch (err) {
    return {ok: false, status: 0, body: {error: err.message}};
  }
}

async function postJson(url, body, userSettings) {
  try {
    const response = await fetchPostAuthSafe(
      url,
      {method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify(body)},
      userSettings.username,
      userSettings.databaseName
    );
    return {ok: response.ok, status: response.status, body: await readJson(response)};
  } catch (err) {
    return {ok: false, status: 0, body: {error: err.message}};
  }
}

/**
 * @return {Promise<object>} {ok, status, body: {canEdit, support, earliestSelfServe, earliestAllowed, timeZone, ...}}
 */
export function getZonePermissions() {
  return getJson('/mapview/zonePermissions');
}

export function applyZoneChange(body, userSettings) {
  return postJson('/mapview/zones/applyChange', body, userSettings);
}

export function previewZoneChange(body, userSettings) {
  return postJson('/mapview/zones/preview', body, userSettings);
}

export function sendZoneChangeToSupport(body, userSettings) {
  return postJson('/mapview/zones/sendToSupport', body, userSettings);
}

/**
 * @return {Promise<object>} {ok, status, body: [{requestId, createdAt, requestedBy, effectiveFrom, reason, blockIds,
 *   areaIds, diff, ticketId}]}, the customer's pending support requests
 */
export function getZoneChangeRequests() {
  return getJson('/mapview/zones/requests');
}

/**
 * Support only: applies a pending request as it was sent.
 *
 * @param {number} requestId
 * @param {string|null} reprocessFrom YYYY-MM-DD, 'none', or null for the change date
 * @param {object} userSettings
 * @return {Promise<object>}
 */
export function applyZoneChangeRequest(requestId, reprocessFrom, userSettings) {
  return postJson(`/mapview/zones/requests/${requestId}/apply`, reprocessFrom ? {reprocessFrom} : {}, userSettings);
}

/**
 * Support only: rejects a pending request.
 *
 * @param {number} requestId
 * @param {string} note why it was rejected (required)
 * @param {object} userSettings
 * @return {Promise<object>}
 */
export function rejectZoneChangeRequest(requestId, note, userSettings) {
  return postJson(`/mapview/zones/requests/${requestId}/reject`, {note}, userSettings);
}

/**
 * @param {Array<object>} requests the pending requests
 * @return {{blocks: object, areas: object}} the first pending request by block id and by other area id
 */
export function requestsByZone(requests) {
  const byZone = {blocks: {}, areas: {}};
  (Array.isArray(requests) ? requests : []).forEach((request) => {
    (request.blockIds || []).forEach((id) => {
      byZone.blocks[id] = byZone.blocks[id] || request;
    });
    (request.areaIds || []).forEach((id) => {
      byZone.areas[id] = byZone.areas[id] || request;
    });
  });
  return byZone;
}

/**
 * @param {object} op
 * @return {boolean} whether the op changes polygon rows and so needs a date
 */
export function isDatedOp(op) {
  if (DATED_OP_TYPES.includes(op.op)) {
    return true;
  }
  if (op.op === 'updateBlock' || op.op === 'updateArea') {
    return Boolean(
      (op.polygons && Object.keys(op.polygons).length > 0) ||
        (op.addPolygons && op.addPolygons.length > 0) ||
        (op.removePolygons && op.removePolygons.length > 0)
    );
  }
  return false;
}

/**
 * Splits ops into labels (saved with no date) and dated ops (saved from the chosen date). An update that does
 * both is split in two. A created block names a field or region created with it by tempId, so those creates
 * stay with the dated ops.
 *
 * @param {Array<object>} ops
 * @return {{labelOps: Array<object>, datedOps: Array<object>}}
 */
export function splitOps(ops) {
  const labelOps = [];
  const datedOps = [];
  ops.forEach((op) => {
    if (!isDatedOp(op)) {
      labelOps.push(op);
      return;
    }
    const labelKey = op.op === 'updateBlock' ? 'block' : op.op === 'updateArea' ? 'area' : null;
    if (labelKey && op[labelKey]) {
      const labelsOnly = {...op};
      delete labelsOnly.polygons;
      delete labelsOnly.addPolygons;
      delete labelsOnly.removePolygons;
      const datedOnly = {...op};
      delete datedOnly[labelKey];
      labelOps.push(labelsOnly);
      datedOps.push(datedOnly);
      return;
    }
    datedOps.push(op);
  });
  const usedTempIds = new Set();
  datedOps.forEach((op) => {
    if (op.op === 'createBlock') {
      [op.block?.fieldId, op.block?.regionId].forEach((id) => {
        if (typeof id === 'string') {
          usedTempIds.add(id);
        }
      });
    }
  });
  const sharedCreates = labelOps.filter((op) => {
    return usedTempIds.has(op.tempId);
  });
  return {
    labelOps: labelOps.filter((op) => {
      return !sharedCreates.includes(op);
    }),
    datedOps: [...sharedCreates, ...datedOps],
  };
}

/**
 * The applyChange / preview / sendToSupport body. The server adds the customer and the actor.
 *
 * @param {Array<object>} ops
 * @param {object} [options]
 * @param {string|null} [options.effectiveFrom] YYYY-MM-DD, sent only when an op is dated
 * @param {string|null} [options.reason]
 * @param {string|null} [options.reprocessFrom] support only: YYYY-MM-DD or 'none'
 * @return {object}
 */
export function buildChangeBody(ops, {effectiveFrom = null, reason = null, reprocessFrom = null} = {}) {
  const dated = ops.some(isDatedOp);
  const trimmedReason = typeof reason === 'string' ? reason.trim() : '';
  return {
    effectiveFrom: dated ? effectiveFrom : null,
    reason: trimmedReason || null,
    reprocessFrom: dated ? reprocessFrom : null,
    ops,
  };
}

/**
 * @param {object} body a 400 response body
 * @return {boolean} whether the change has to go to support
 */
export function needsSupportError(body) {
  return (body?.errors || []).some((error) => {
    return error?.code === 'needs_support';
  });
}

/**
 * @param {object} body a 400 body or a preview
 * @param {Function} t
 * @return {Array<string>} messages to show; needs_support is left out (the dialog switches to Send to support)
 */
export function errorMessages(body, t) {
  const known = {
    future_date: t('Pick today or an earlier date.'),
    reason_required: t('A reason is required for a change dated this far back.'),
  };
  const messages = (body?.errors || [])
    .filter((error) => {
      return error?.code !== 'needs_support';
    })
    .map((error) => {
      return known[error.code] || error.message || error.code;
    })
    .filter(Boolean);
  if (messages.length === 0 && body?.error) {
    messages.push(typeof body.error === 'string' ? body.error : JSON.stringify(body.error));
  }
  return messages;
}

/**
 * @param {string} timeZone the customer's time zone
 * @return {string} today in the customer's time zone, YYYY-MM-DD
 */
export function todayLocal(timeZone) {
  return DateTime.now()
    .setZone(timeZone || 'America/Los_Angeles')
    .toISODate();
}

function isValidDate(value) {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) && DateTime.fromISO(value).isValid;
}

/**
 * What the save dialog offers for a date (contract section 2). Dates are YYYY-MM-DD strings, which compare
 * correctly as strings.
 *
 * - apply: the user can apply it. Support may need a reason (backdated, or before the freeze).
 * - support: everyone else, earlier than they may apply: Send to support with a reason.
 * - future / invalid: refused.
 *
 * @param {object} args
 * @param {string} args.date YYYY-MM-DD
 * @param {string} args.today YYYY-MM-DD
 * @param {object} args.permissions the zonePermissions response
 * @return {{status: string, reasonRequired: boolean, freezeBypass: boolean, beforeSelfServe: boolean}}
 */
export function decideDate({date, today, permissions}) {
  const none = {reasonRequired: false, freezeBypass: false, beforeSelfServe: false};
  if (!isValidDate(date)) {
    return {...none, status: 'invalid'};
  }
  if (date > today) {
    return {...none, status: 'future'};
  }
  // From today rather than the server's value, which was fetched when Mapview opened and goes stale overnight (the
  // server still enforces it)
  const earliestSelfServe = DateTime.fromISO(today, {zone: 'utc'}).minus({days: SELF_SERVE_DAYS}).toISODate();
  const beforeSelfServe = date < earliestSelfServe;
  if (permissions?.support) {
    const freeze = permissions.freezeDate && permissions.freezeDate !== NO_FREEZE ? permissions.freezeDate : null;
    const beforeFreeze = Boolean(freeze) && date < freeze;
    return {status: 'apply', reasonRequired: beforeSelfServe || beforeFreeze, freezeBypass: beforeFreeze, beforeSelfServe};
  }
  // The server's earliestAllowed is the later of the freeze and its (possibly stale) self-serve date
  const earliestAllowed =
    permissions?.earliestAllowed && permissions.earliestAllowed > earliestSelfServe
      ? permissions.earliestAllowed
      : earliestSelfServe;
  if (date >= earliestAllowed) {
    return {...none, status: 'apply'};
  }
  return {status: 'support', reasonRequired: true, freezeBypass: false, beforeSelfServe};
}
